//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import { isEmpty } from "lodash";
import React, { useState } from "react";
import { Button, Checkbox, Label, Message, Popup, Table } from "semantic-ui-react";
import { Confirm, ConfirmInfo, UserLink } from "../../ui/lib";
import { DateTimePopupWithUTCAndLocalTime } from "../../util/dateAndTimeUtils/DateTime";
import AssignedFiltersForJobView from "../deployment-updater/AssignedFiltersForJobView";
import { SeverityLabel } from "./severity";
import { NotificationRule, NotificationRuleTarget, filtersTitle, ruleScope, targetLabels } from "./types";

const isExpired = (rule: NotificationRule) => !!rule.notification.expiresAt && rule.notification.expiresAt.getTime() < Date.now();

// Inactive (disabled or expired) rules are only greyed out: Table.Row's disabled prop sets pointer-events: none,
// which would also block the toggle, edit, delete and audit log actions of that row.
const inactiveRowStyle = { color: "rgba(40, 40, 40, 0.4)" };

// RulesView lists notification rules and lets editors enable, disable, edit or delete them.
// Enabling, disabling and editing need the update permission of the rule's target, deleting the delete
// permission; without them the rule is shown read-only.
export const RulesView = ({
  rules,
  canUpdate,
  canDelete,
  onToggle,
  onEdit,
  onDelete,
  onShowHistory,
}: {
  rules: NotificationRule[];
  canUpdate: Record<NotificationRuleTarget, boolean>;
  canDelete: Record<NotificationRuleTarget, boolean>;
  onToggle: (rule: NotificationRule, enabled: boolean) => void;
  onEdit: (rule: NotificationRule) => void;
  onDelete: (rule: NotificationRule) => void;
  onShowHistory: (rule: NotificationRule) => void;
}) => {
  const [scopeRule, setScopeRule] = useState<NotificationRule | undefined>(undefined);
  const [confirmInfo, setConfirmInfo] = useState<ConfirmInfo | undefined>(undefined);

  const handleDelete = (rule: NotificationRule) => {
    setConfirmInfo({
      header: `Delete rule ${rule.id}?`,
      warning: `The notification "${rule.notification.notificationId}" will be removed from every ${rule.target} that currently carries it (including any set manually with the same ID).`,
      onConfirm: async () => {
        onDelete(rule);
        setConfirmInfo(undefined);
      },
      onDenied: () => setConfirmInfo(undefined),
    });
  };

  return (
    <>
      <Confirm confirmInfo={confirmInfo} />
      {scopeRule && (
        <AssignedFiltersForJobView deploymentFilters={ruleScope(scopeRule)} title={filtersTitle[scopeRule.target]} onClose={() => setScopeRule(undefined)} />
      )}
      <Table striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>ID</Table.HeaderCell>
            <Table.HeaderCell>Target</Table.HeaderCell>
            <Table.HeaderCell>Notification ID</Table.HeaderCell>
            <Table.HeaderCell>Severity</Table.HeaderCell>
            <Table.HeaderCell>Text</Table.HeaderCell>
            <Table.HeaderCell>Scope</Table.HeaderCell>
            <Table.HeaderCell>Expires at</Table.HeaderCell>
            <Table.HeaderCell>Matched</Table.HeaderCell>
            <Table.HeaderCell>Last reconciled</Table.HeaderCell>
            <Table.HeaderCell>Created</Table.HeaderCell>
            <Table.HeaderCell>Enabled</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rules.map((rule) => (
            <Table.Row key={`${rule.target}-${rule.id}`} style={!rule.enabled || isExpired(rule) ? inactiveRowStyle : undefined}>
              <Table.Cell>{rule.id}</Table.Cell>
              <Table.Cell>{targetLabels[rule.target]}</Table.Cell>
              <Table.Cell>
                <code>{rule.notification.notificationId}</code>
              </Table.Cell>
              <Table.Cell>
                <SeverityLabel severity={rule.notification.severity} />
              </Table.Cell>
              <Table.Cell>
                <Popup
                  wide="very"
                  content={rule.notification.notificationText}
                  trigger={<span>{rule.notification.notificationText.slice(0, 60)}&hellip;</span>}
                />
              </Table.Cell>
              <Table.Cell>
                {isEmpty(ruleScope(rule)) ? (
                  `All ${rule.target}s`
                ) : (
                  <Button basic size="tiny" icon="eye" content={`${Object.keys(ruleScope(rule)).length} filter(s)`} onClick={() => setScopeRule(rule)} />
                )}
              </Table.Cell>
              <Table.Cell>
                {rule.notification.expiresAt ? (
                  <>
                    <DateTimePopupWithUTCAndLocalTime dateTime={rule.notification.expiresAt.toISOString()} label="Expires at" />
                    {isExpired(rule) && (
                      <Label size="tiny" basic>
                        expired
                      </Label>
                    )}
                  </>
                ) : (
                  "-"
                )}
              </Table.Cell>
              <Table.Cell>{rule.matched}</Table.Cell>
              <Table.Cell>
                {rule.last_reconciled_at ? <DateTimePopupWithUTCAndLocalTime dateTime={rule.last_reconciled_at.toISOString()} label="Last reconciled" /> : "-"}
              </Table.Cell>
              <Table.Cell>
                {rule.created_at && <DateTimePopupWithUTCAndLocalTime dateTime={rule.created_at.toISOString()} label="Created at" />}
                {rule.created_by_id && (
                  <>
                    <br />
                    <small>
                      <UserLink id={rule.created_by_id} />
                    </small>
                  </>
                )}
                {rule.updated_by_id && (
                  <>
                    <br />
                    <small>
                      updated by <UserLink id={rule.updated_by_id} />
                    </small>
                  </>
                )}
              </Table.Cell>
              <Table.Cell>
                {canUpdate[rule.target] ? (
                  <Checkbox toggle checked={rule.enabled} onChange={(event, data) => onToggle(rule, !!data.checked)} />
                ) : rule.enabled ? (
                  "Yes"
                ) : (
                  "No"
                )}
              </Table.Cell>
              <Table.Cell>
                <Button basic size="tiny" icon="history" title="Show audit log" onClick={() => onShowHistory(rule)} />
                {canUpdate[rule.target] && <Button basic size="tiny" icon="edit" title="Edit rule" onClick={() => onEdit(rule)} />}
                {canDelete[rule.target] && <Button basic size="tiny" icon="trash" title="Delete rule" onClick={() => handleDelete(rule)} />}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {!rules.length && <Message>No platform notification rules</Message>}
    </>
  );
};
